import * as React from 'react';

import {
  DataGridColumn,
  LocalizedDate,
  maybeEmptyInfo,
  Menu,
  Button,
} from '@rematter/ui-web';
import { MoreVertical, Edit, Play, Pause, Trash2, Copy } from 'lucide-react';
import { useLocation } from 'wouter';

import { useTranslations } from '../../../i18n/useTranslations';
import { automationsRoutes } from '../../../routes';
import { AutomationRecord } from '../../../types';
import { AutomationStatusBadge } from '../AutomationStatusBadge';

type MenuItem = {
  label: string | React.ReactNode;
  onAction: () => void;
  disabled?: boolean;
};

type UseAutomationsListColumnsProps = {
  onActivate?: (automationId: string) => void;
  onPause?: (automationId: string) => void;
  onDelete?: (automationId: string) => void;
  onDuplicate?: (automationId: string) => void;
  onRefetch?: () => void;
};

export const useAutomationsListColumns = ({
  onActivate,
  onPause,
  onDelete,
  onDuplicate,
  onRefetch,
}: UseAutomationsListColumnsProps) => {
  const t = useTranslations();
  const [, navigate] = useLocation();

  const handleCellClick = React.useCallback(
    ({ row }: { row: AutomationRecord }) => {
      navigate(automationsRoutes.detail.replace(':id', row.id));
    },
    [navigate],
  );

  const handleEdit = React.useCallback(
    (automation: AutomationRecord) => {
      navigate(automationsRoutes.detail.replace(':id', automation.id));
    },
    [navigate],
  );

  const getMenuItems = React.useCallback(
    (row: AutomationRecord): MenuItem[] => {
      const items: MenuItem[] = [
        {
          label: (
            <div className="flex items-center gap-2">
              <Edit className="size-4" />
              {t.detail.actions.edit}
            </div>
          ),
          onAction: () => handleEdit(row),
        },
      ];

      if (row.status === 'Active' || row.status === 'Paused') {
        items.push({
          label: (
            <div className="flex items-center gap-2">
              {row.status === 'Active' ? (
                <Pause className="size-4" />
              ) : (
                <Play className="size-4" />
              )}
              {row.status === 'Active'
                ? t.detail.actions.pause
                : t.detail.actions.activate}
            </div>
          ),
          onAction: () => {
            if (row.status === 'Active') {
              onPause?.(row.id);
            } else {
              onActivate?.(row.id);
            }
            onRefetch?.();
          },
        });
      }

      if (row.status === 'Draft') {
        items.push({
          label: (
            <div className="flex items-center gap-2">
              <Play className="size-4" />
              {t.detail.actions.activate}
            </div>
          ),
          onAction: () => {
            onActivate?.(row.id);
            onRefetch?.();
          },
        });
      }

      items.push({
        label: (
          <div className="flex items-center gap-2">
            <Copy className="size-4" />
            {t.detail.actions.duplicate}
          </div>
        ),
        onAction: () => {
          onDuplicate?.(row.id);
        },
      });

      items.push({
        label: (
          <div className="flex items-center gap-2 text-failure">
            <Trash2 className="size-4" />
            {t.detail.actions.delete}
          </div>
        ),
        onAction: () => {
          onDelete?.(row.id);
          onRefetch?.();
        },
      });

      return items;
    },
    [handleEdit, onActivate, onPause, onDelete, onDuplicate, onRefetch, t],
  );

  const columns = React.useMemo(() => {
    const columnDefs: DataGridColumn<AutomationRecord>[] = [
      {
        key: 'name',
        header: t.columns.name,
        cell: ({ row }) => (
          <div className="font-medium text-primary hover:underline">
            {maybeEmptyInfo(row.name)}
          </div>
        ),
      },
      {
        key: 'status',
        header: t.columns.status,
        cell: ({ row }) => (
          <AutomationStatusBadge status={row.status} size="small" />
        ),
      },
      {
        key: 'triggerType',
        header: t.columns.triggerType,
        cell: ({ row }) => maybeEmptyInfo(t.triggerType[row.triggerType]),
      },
      {
        key: 'description',
        header: t.columns.description,
        width: 300,
        cell: ({ row }) => (
          <div
            className="truncate text-sm text-neutral/60"
            title={row.description ?? undefined}
          >
            {maybeEmptyInfo(row.description)}
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: t.columns.createdAt,
        cell: ({ row }) =>
          maybeEmptyInfo(row.timestamps?.createdAt, (v) => (
            <LocalizedDate date={v} />
          )),
      },
      {
        key: 'updatedAt',
        header: t.columns.updatedAt,
        cell: ({ row }) =>
          maybeEmptyInfo(row.timestamps?.updatedAt, (v) => (
            <LocalizedDate date={v} />
          )),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row }) => (
          <div className="flex justify-center">
            <Menu
              trigger={
                <Button
                  size="icon"
                  icon={<MoreVertical className="size-4" />}
                  variant="outline"
                />
              }
              items={getMenuItems(row)}
            />
          </div>
        ),
      },
    ];

    return columnDefs.map((it) => ({
      ...it,
      onClick: it.key === 'actions' ? undefined : handleCellClick,
    }));
  }, [handleCellClick, getMenuItems, t]);

  return { columns };
};
